/**
 * Student settings:
 *  C10 · Practice reminders (days + time)
 *  F3  · Notification preferences + in-app feed
 *  J2  · Mic latency calibration
 *  J10 · Language (en/de)
 *  A10 · Data export & account deletion
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { NOTIFICATION_TYPES, VOICE_TYPES } from '@solfo/shared';
import { api, getToken } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { setLocale, t } from '../../lib/i18n.ts';
import { calibrateLatency } from '../../audio/mic.ts';
import PushCard from '../../components/PushCard.tsx';
import { Button, Card, ErrorText, Field, Input, Modal, Select, Spinner, Tag, Textarea, cls, fmtDateTime } from '../../lib/ui.tsx';

const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

interface Reminders {
  daysOfWeek: string;
  timeOfDay: string;
  enabled: boolean;
}

interface NotificationPref {
  type: (typeof NOTIFICATION_TYPES)[number];
  inApp: boolean;
  email: boolean;
  push: boolean;
}

interface NotificationRow {
  id: string;
  type: string;
  title: string;
  body: string;
  readAt: string | null;
  createdAt: string;
}

interface StudentProfileData {
  goals: string;
  voiceType: string | null;
  coach: { id: string; name: string; email: string } | null;
}

export default function SettingsPage() {
  const { me, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);

  // ── Profile & coach ────────────────────────────────────────────────────
  const { data: profile } = useQuery({
    queryKey: ['student-profile', me?.id],
    queryFn: () => api<StudentProfileData>(`/api/students/${me!.id}`),
    enabled: !!me,
  });
  const [goals, setGoals] = useState('');
  const [voiceType, setVoiceType] = useState('');
  useEffect(() => {
    if (profile) {
      setGoals(profile.goals);
      setVoiceType(profile.voiceType ?? '');
    }
  }, [profile]);

  const saveProfile = useMutation({
    mutationFn: () =>
      api(`/api/students/${me!.id}`, { method: 'PATCH', body: { goals, voiceType: voiceType || null } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['student-profile', me?.id] }),
    onError: setError,
  });

  const saveLocale = useMutation({
    mutationFn: async (locale: string) => {
      await api('/api/me', { method: 'PATCH', body: { locale } });
      setLocale(locale);
      await refresh();
    },
    onError: setError,
  });

  // ── C10 · Reminders ────────────────────────────────────────────────────
  const { data: reminders } = useQuery({
    queryKey: ['reminders'],
    queryFn: () => api<Reminders>('/api/me/reminders'),
  });
  const [rem, setRem] = useState<Reminders | null>(null);
  useEffect(() => {
    if (reminders) setRem(reminders);
  }, [reminders]);

  const saveReminders = useMutation({
    mutationFn: (body: Reminders) => api('/api/me/reminders', { method: 'PUT', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['reminders'] }),
    onError: setError,
  });
  const updateRem = (patch: Partial<Reminders>) => {
    if (!rem) return;
    const next = { ...rem, ...patch };
    setRem(next);
    saveReminders.mutate(next);
  };
  const remDays = new Set((rem?.daysOfWeek ?? '').split(',').filter(Boolean));

  // ── F3 · Notification prefs ────────────────────────────────────────────
  const { data: prefs } = useQuery({
    queryKey: ['notification-prefs'],
    queryFn: () => api<NotificationPref[]>('/api/me/notification-prefs'),
  });
  const savePrefs = useMutation({
    mutationFn: (body: NotificationPref[]) => api('/api/me/notification-prefs', { method: 'PUT', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notification-prefs'] }),
    onError: setError,
  });
  const togglePref = (type: NotificationPref['type'], channel: 'inApp' | 'email' | 'push') => {
    if (!prefs) return;
    savePrefs.mutate(prefs.map((p) => (p.type === type ? { ...p, [channel]: !p[channel] } : p)));
  };

  // ── Notifications feed ─────────────────────────────────────────────────
  const { data: notifications } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api<NotificationRow[]>('/api/notifications'),
  });
  // ── J2 · Mic calibration ───────────────────────────────────────────────
  const [calibrating, setCalibrating] = useState(false);
  const [latencyMs, setLatencyMs] = useState<number | null>(() => {
    const stored = localStorage.getItem('solfo.latencyMs');
    return stored !== null ? Number(stored) : null;
  });
  async function runCalibration() {
    setCalibrating(true);
    setError(null);
    try {
      const ms = await calibrateLatency();
      localStorage.setItem('solfo.latencyMs', String(ms));
      setLatencyMs(ms);
      await api('/api/me/calibrations', {
        method: 'PUT',
        body: { deviceLabel: navigator.userAgent.slice(0, 80), offsetMs: ms },
      });
    } catch (err) {
      setError(err);
    } finally {
      setCalibrating(false);
    }
  }

  // ── A10 · Export & delete ──────────────────────────────────────────────
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [password, setPassword] = useState('');
  async function downloadExport() {
    setError(null);
    try {
      const res = await fetch('/api/me/export', { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'solfo-export.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err);
    }
  }
  const deleteAccount = useMutation({
    mutationFn: () => api('/api/me', { method: 'DELETE', body: { password } }),
    onSuccess: () => {
      logout();
      navigate('/login');
    },
    onError: setError,
  });

  if (!me) return <Spinner />;

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-bold">{t('settings.title', 'Settings')}</h1>

      {/* Profile */}
      <Card title={t('settings.profile', 'Profile')}>
        <div className="space-y-3">
          <Field label={t('settings.goals', 'My goals')}>
            <Textarea rows={3} value={goals} onChange={(e) => setGoals(e.target.value)} placeholder="e.g. belt up to a C5 without strain" />
          </Field>
          <Field label={t('settings.voiceType', 'Voice type')}>
            <Select value={voiceType} onChange={(e) => setVoiceType(e.target.value)}>
              <option value="">{t('settings.voiceUnset', 'Not set')}</option>
              {Object.keys(VOICE_TYPES).map((vt) => (
                <option key={vt} value={vt}>
                  {vt}
                </option>
              ))}
            </Select>
          </Field>
          <Button onClick={() => saveProfile.mutate()} disabled={saveProfile.isPending}>
            {t('settings.save', 'Save')}
          </Button>
          <Field label={t('settings.language', 'Language')}>
            <Select value={me.locale} onChange={(e) => saveLocale.mutate(e.target.value)}>
              <option value="en">English</option>
              <option value="de">Deutsch</option>
            </Select>
          </Field>
        </div>
      </Card>

      {/* My coach */}
      {profile?.coach && (
        <Card title={t('settings.coach', 'My coach')}>
          <p className="text-sm font-semibold">{profile.coach.name}</p>
          <p className="text-xs text-slate-500">{profile.coach.email}</p>
        </Card>
      )}

      {/* C10 · practice reminders */}
      <Card
        title={t('settings.reminders', 'Practice reminders')}
        actions={
          rem && (
            <button
              role="switch"
              aria-checked={rem.enabled}
              onClick={() => updateRem({ enabled: !rem.enabled })}
              className={cls(
                'relative h-6 w-11 rounded-full transition-colors',
                rem.enabled ? 'bg-brand-600' : 'bg-slate-300',
              )}
            >
              <span
                className={cls(
                  'absolute top-0.5 left-0.5 size-5 rounded-full bg-white shadow transition-transform',
                  rem.enabled && 'translate-x-5',
                )}
              />
            </button>
          )
        }
      >
        {!rem ? (
          <Spinner />
        ) : (
          <div className={cls('space-y-3', !rem.enabled && 'opacity-50')}>
            <div className="flex flex-wrap gap-1.5">
              {DAYS.map((day) => (
                <button
                  key={day}
                  disabled={!rem.enabled}
                  onClick={() => {
                    const next = new Set(remDays);
                    if (next.has(day)) next.delete(day);
                    else next.add(day);
                    updateRem({ daysOfWeek: DAYS.filter((d) => next.has(d)).join(',') });
                  }}
                  className={cls(
                    'rounded-full px-3 py-1.5 text-xs font-medium capitalize transition-colors',
                    remDays.has(day) ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600',
                  )}
                >
                  {day}
                </button>
              ))}
            </div>
            <Field label={t('settings.reminderTime', 'Time of day')}>
              <Input
                type="time"
                value={rem.timeOfDay}
                disabled={!rem.enabled}
                onChange={(e) => updateRem({ timeOfDay: e.target.value })}
              />
            </Field>
          </div>
        )}
      </Card>

      {/* F3 · notification preferences */}
      <Card title={t('settings.notifPrefs', 'Notification preferences')}>
        {!prefs ? (
          <Spinner />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400">
                <th className="pb-2 font-medium">{t('settings.notifType', 'Type')}</th>
                <th className="pb-2 text-center font-medium">{t('settings.inApp', 'In-app')}</th>
                <th className="pb-2 text-center font-medium">{t('settings.email', 'Email')}</th>
                <th className="pb-2 text-center font-medium">{t('settings.pushCol', 'Push')}</th>
              </tr>
            </thead>
            <tbody>
              {prefs.map((p) => (
                <tr key={p.type} className="border-t border-slate-100">
                  <td className="py-1.5 text-xs text-slate-700">{p.type.replace(/_/g, ' ')}</td>
                  <td className="py-1.5 text-center">
                    <input type="checkbox" checked={p.inApp} onChange={() => togglePref(p.type, 'inApp')} className="accent-brand-600" />
                  </td>
                  <td className="py-1.5 text-center">
                    <input type="checkbox" checked={p.email} onChange={() => togglePref(p.type, 'email')} className="accent-brand-600" />
                  </td>
                  <td className="py-1.5 text-center">
                    <input type="checkbox" checked={p.push} onChange={() => togglePref(p.type, 'push')} className="accent-brand-600" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* F3 · per-device push opt-in */}
      <PushCard />

      {/* J2 · mic calibration */}
      <Card title={t('settings.calibration', 'Mic calibration')}>
        <p className="text-xs text-slate-500">
          {t(
            'settings.calibrationHint',
            'Headphones and Bluetooth add delay between what you sing and what we hear. Calibration plays a click and measures that delay so your pitch trace lines up perfectly.',
          )}
        </p>
        <div className="mt-3 flex items-center gap-3">
          <Button variant="secondary" onClick={() => void runCalibration()} disabled={calibrating}>
            {calibrating ? (
              <span className="flex items-center gap-2">
                <span className="size-3 animate-spin rounded-full border-2 border-slate-300 border-t-brand-600" />
                {t('settings.calibrating', 'Listening…')}
              </span>
            ) : (
              t('settings.calibrate', 'Run calibration')
            )}
          </Button>
          {latencyMs !== null && (
            <span className="text-xs text-slate-600">
              {t('settings.currentLatency', 'Current offset:')} <strong>{latencyMs} ms</strong>
            </span>
          )}
        </div>
      </Card>

      {/* Quick links */}
      <Card>
        <div className="divide-y divide-slate-100">
          <Link to="/app/warmup" className="flex items-center justify-between py-2 text-sm text-slate-700 hover:text-brand-700">
            {t('settings.warmup', 'Warm-up')} <span>→</span>
          </Link>
        </div>
      </Card>

      {/* A10 · data & account */}
      <Card title={t('settings.data', 'Data & account')}>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => void downloadExport()}>
            {t('settings.export', 'Download my data')}
          </Button>
          <Button variant="danger" onClick={() => setDeleteOpen(true)}>
            {t('settings.delete', 'Delete account')}
          </Button>
        </div>
      </Card>

      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} title={t('settings.deleteTitle', 'Delete account')}>
        <p className="text-sm text-slate-600">
          {t(
            'settings.deleteHint',
            'This permanently removes your personal data. Enter your password to confirm.',
          )}
        </p>
        <div className="mt-3 space-y-3">
          <Input
            type="password"
            placeholder={t('settings.password', 'Password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button variant="danger" disabled={!password || deleteAccount.isPending} onClick={() => deleteAccount.mutate()}>
            {t('settings.deleteConfirm', 'Delete my account')}
          </Button>
        </div>
      </Modal>

      <ErrorText error={error} />
    </div>
  );
}
